import React, { useEffect, useRef, useState } from "react";
import {
  useParams,
  useSearchParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFetchSingleAgendaQuery } from "@store/api/agenda";
import { useGetActivitiesQuery } from "@store/api/activities";
import { useProfile } from "@hooks";
import { AgendaLoader } from "@components";
import { removeFromLocalStorage } from "@utils/localStorage";
import { hasDemoGuestToken } from "@utils/guestDemo";
import { clearActivityState } from "@store/activitySlice";
import { setDemoStep } from "@store/demoSlice";
import NewAgendaView from "./NewAgendaView";
import PastAgendaView from "./PastAgendaView";
import { AgendaRevealSummary } from "./AgendaReveal";

/**
 * Wrapper component that determines whether to show NewAgendaView or PastAgendaView
 * based on whether the agenda has ended (has endTime)
 */
const AgendaDetailView = () => {
  const { agendaId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { profile } = useProfile();

  // Demo flow state — read here (the wrapper) so the GUIDE_STOP -> report
  // hand-off survives the NewAgendaView -> PastAgendaView swap that happens the
  // moment the agenda gains an endTime. useDemoFlow's own GUIDE_STOP effect
  // lives inside NewAgendaView, which unmounts on that swap, so the guided
  // auto-navigation (and therefore REPORT_CTA) would never fire without this.
  const demoIsActive = useSelector((state) => state.demo.isActive);
  const demoCurrentStep = useSelector((state) => state.demo.currentStep);
  const guidedReportNavDoneRef = useRef(false);

  // State for Agenda Reveal modal
  const [showRevealModal, setShowRevealModal] = useState(false);
  const [revealActivities, setRevealActivities] = useState([]);
  const [revealMeetingType, setRevealMeetingType] = useState(null);
  const [autoResumeTriggered, setAutoResumeTriggered] = useState(false);

  // Check if we should show the reveal modal from navigation state
  useEffect(() => {
    if (location.state?.showAgendaReveal) {
      setShowRevealModal(true);
      setRevealActivities(location.state.revealActivities || []);
      setRevealMeetingType(location.state.meetingType || null);
      setAutoResumeTriggered(true);

      // Clear the state from history to prevent showing modal on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Safety cleanup: Clear smart agenda draft if coming from smart agenda creation
  useEffect(() => {
    const isFromSmartAgenda = searchParams.get("smart") === "true";
    if (isFromSmartAgenda) {
      removeFromLocalStorage("smart-agenda-draft");
    }
  }, [searchParams]);

  // Handle closing the reveal modal
  const handleCloseRevealModal = () => {
    setShowRevealModal(false);
  };

  // A guest demo has no authenticated profile; it is authorized by its guest
  // token instead, so allow the demo-runtime queries to run in that case.
  const isGuestDemo = hasDemoGuestToken();
  const canFetch = !!profile?._id || isGuestDemo;

  // Fetch agenda and activities once at this level
  const { data: currentAgenda, isLoading: fetchingAgenda } =
    useFetchSingleAgendaQuery(agendaId, {
      skip: !canFetch,
    });

  const { data: activities, isLoading: fetchingActivities } =
    useGetActivitiesQuery({ agendaId }, { skip: !canFetch });

  // Determine if this is a past agenda (has endTime)
  const isPastAgenda = !!currentAgenda?.endTime;

  // Guided demo hand-off to the report on GUIDE_STOP. Lives in the wrapper
  // because NewAgendaView (which owns useDemoFlow) unmounts once the agenda is
  // past, which would otherwise strand the flow before the CTA.
  useEffect(() => {
    if (guidedReportNavDoneRef.current) return;
    if (!demoIsActive || demoCurrentStep !== "GUIDE_STOP") return;
    if (!isPastAgenda || !currentAgenda?._id) return;

    guidedReportNavDoneRef.current = true;

    // Dispatch before navigating and NOT inside a setTimeout — navigate()
    // unmounts this wrapper and a timer's cleanup would cancel the dispatch.
    dispatch(setDemoStep("REPORT_CTA"));

    const reportPath = hasDemoGuestToken()
      ? `/demo/${currentAgenda._id}/report?demo=true`
      : `/pro/reports/${currentAgenda._id}?demo=true`;
    navigate(reportPath);
  }, [
    demoIsActive,
    demoCurrentStep,
    isPastAgenda,
    currentAgenda?._id,
    navigate,
    dispatch,
  ]);

  // Clear activity state when navigating between agendas to prevent stale data
  const prevAgendaIdRef = useRef(agendaId);
  useEffect(() => {
    if (prevAgendaIdRef.current !== agendaId) {
      dispatch(clearActivityState());
      prevAgendaIdRef.current = agendaId;
    }
  }, [agendaId, dispatch]);

  // Effect to auto-trigger reveal modal for pending activities
  // Must be before any early returns to follow React hooks rules
  useEffect(() => {
    if (
      autoResumeTriggered ||
      showRevealModal ||
      isPastAgenda ||
      !currentAgenda ||
      !Array.isArray(activities)
    ) {
      return;
    }

    const hasPendingReveal = activities.some(
      (activity) => activity?.smartAgendaRevealStatus === "pending",
    );

    if (hasPendingReveal && !location.state?.showAgendaReveal) {
      setRevealActivities(activities);
      setRevealMeetingType(currentAgenda?.blueprint?.meetingType || null);
      setShowRevealModal(true);
      setAutoResumeTriggered(true);
    }
  }, [
    activities,
    autoResumeTriggered,
    currentAgenda,
    isPastAgenda,
    location.state,
    showRevealModal,
  ]);

  // Show loader while fetching agenda
  if (fetchingAgenda || !currentAgenda) {
    return <AgendaLoader />;
  }

  // Render appropriate view based on agenda state
  return (
    <>
      {isPastAgenda ? (
        <PastAgendaView
          agenda={currentAgenda}
          activities={activities}
          isLoading={fetchingActivities}
        />
      ) : (
        <NewAgendaView
          agenda={currentAgenda}
          activities={activities}
          isLoading={fetchingActivities}
        />
      )}

      {/* Agenda Reveal Summary - shown after smart agenda creation */}
      {showRevealModal && currentAgenda && (
        <AgendaRevealSummary
          agenda={currentAgenda}
          activities={revealActivities}
          meetingType={revealMeetingType}
          onComplete={handleCloseRevealModal}
          onClose={handleCloseRevealModal}
        />
      )}
    </>
  );
};

export default AgendaDetailView;
